import type { TextStyle, ViewStyle } from "react-native";

import type { UiTheme } from "../theme";

// The visual grammar the two meter families SHARE — the track chrome and the
// row shell. Written by hand twice (TargetBar/LandmarkBar, and the target and
// band meter rows, the band one gone in PG-64 fetta 2) with byte-identical
// token values, which is
// the moment §CONSOLIDAMENTO says to build the primitive instead of keeping the
// copy. Sibling in form of `makeCardBoxStyle` (cardBoxStyle.ts): a style helper
// that hands back raw objects for the caller's own StyleSheet.create, never a
// component.
//
// ⛔ WHAT THIS DOES NOT SHARE, and must not. The two meters answer different
// questions against different references: `TargetBar` scores against a goal the
// athlete wrote (3 zones, one caret), `LandmarkBar` against a published band
// (4 zones, three boundary ticks). Their GEOMETRY (`targetBarGeom` vs
// `landmarkBarGeom`), their zone colours and their glyphs stay apart on purpose
// — see LandmarkBar's header and §16.4bis. What is shared here is only the
// chrome: how a track looks, how a row is laid out. A palette or spacing tweak
// then lands once instead of twice, and the two bars cannot silently drift into
// looking like two different widgets in one list.

/** The bar's track and its fill — the box the meter is drawn in.
 *  ⛔ Typed, not `as const`: an `as const` freezes `fontVariant` into a readonly
 *  tuple that RN's own `TextStyle` will not accept. */
export function makeMeterTrackStyle(theme: UiTheme): {
  track: ViewStyle;
  fill: ViewStyle;
} {
  const { palette: p, radius: r } = theme;
  return {
    track: {
      height: 10,
      backgroundColor: p.surface2,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r1,
      overflow: "hidden",
    },
    fill: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      borderRadius: r.r1,
    },
  };
}

/** The row above the bar: name on the left, the value cluster on the right. */
export function makeMeterRowShellStyle(theme: UiTheme): {
  rowTop: ViewStyle;
  valueWrap: ViewStyle;
  value: TextStyle;
  state: TextStyle;
} {
  const { palette: p, spacing: s, typography: ty } = theme;
  return {
    rowTop: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s3,
    },
    // flexShrink + wrap so a long tonnage value ("5,000 kg / 6,000 kg") next to
    // the state word can yield or wrap instead of pushing past the edge.
    valueWrap: {
      flexDirection: "row",
      alignItems: "baseline",
      flexShrink: 1,
      minWidth: 0,
      flexWrap: "wrap",
      justifyContent: "flex-end",
      gap: s.s2,
    },
    value: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
      // The number never wraps or shrinks; only `state` yields (#339).
      flexShrink: 0,
    },
    // flexShrink + minWidth 0 (#339): a long translated word ("nicht abgedeckt")
    // wraps inside the row instead of leaving its right edge at 320 px.
    state: {
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      flexShrink: 1,
      minWidth: 0,
      textAlign: "right",
    },
  };
}
